import { Suspense, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { PlayerProfileSheetPlayer } from "./PlayerProfileSheet";
import { lazyWithRefresh } from "@/lib/lazyWithRefresh";

const PlayerProfileSheet = lazyWithRefresh(() =>
  import("./PlayerProfileSheet").then((m) => ({ default: m.PlayerProfileSheet }))
);

type PlayerProfileContextValue = {
  openPlayerProfile: (player: PlayerProfileSheetPlayer) => void;
  closePlayerProfile: () => void;
};

const PlayerProfileContext = createContext<PlayerProfileContextValue | null>(null);

export function PlayerProfileSheetProvider({ children }: { children: ReactNode }) {
  const [profilePlayer, setProfilePlayer] = useState<PlayerProfileSheetPlayer | null>(null);
  // Mounted on first open, not up front: the sheet chunk pulls in recharts (~110KB gzipped),
  // which every page would otherwise download while the sheet stays closed.
  const [sheetMounted, setSheetMounted] = useState(false);
  const historyPushed = useRef(false);

  const closeSheet = useCallback(() => {
    setProfilePlayer(null);
    historyPushed.current = false;
  }, []);

  const openPlayerProfile = useCallback((player: PlayerProfileSheetPlayer) => {
    setSheetMounted(true);
    setProfilePlayer(player);
    if (historyPushed.current) {
      // Sheet already open — replace current entry so swapping players doesn't stack history.
      // A single back press then closes the sheet regardless of how many players were viewed.
      // Spread existing state to preserve React Router's idx/key metadata.
      try {
        window.history.replaceState({ ...window.history.state, profileSheet: true }, "");
      } catch {
        // SecurityError in sandboxed iframes — sheet still opens, back-button integration disabled.
      }
    } else {
      try {
        window.history.pushState({ ...window.history.state, profileSheet: true }, "");
        historyPushed.current = true;
      } catch {
        // SecurityError in sandboxed iframes — sheet still opens, back-button integration disabled.
        historyPushed.current = false;
      }
    }
  }, []);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Only close when navigating away from our pushed entry (landing state has no profileSheet key).
      // This prevents spurious closure on unrelated React Router navigations that also trigger popstate.
      if (!event.state?.profileSheet) {
        closeSheet();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [closeSheet]);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      const needsBack = historyPushed.current;
      closeSheet(); // clears historyPushed before back() fires popstate
      if (needsBack) {
        try {
          window.history.back();
        } catch {
          // Silently ignore — sheet is already visually closed.
        }
      }
    }
  }, [closeSheet]);

  // Route closePlayerProfile through handleOpenChange so history is always cleaned up correctly.
  const closePlayerProfile = useCallback(() => handleOpenChange(false), [handleOpenChange]);

  const contextValue = useMemo(
    () => ({ openPlayerProfile, closePlayerProfile }),
    [openPlayerProfile, closePlayerProfile]
  );

  return (
    <PlayerProfileContext.Provider value={contextValue}>
      {children}
      {sheetMounted && (
        <Suspense fallback={null}>
          <PlayerProfileSheet
            open={!!profilePlayer}
            onOpenChange={handleOpenChange}
            player={profilePlayer}
            onPlayerNavigate={openPlayerProfile}
          />
        </Suspense>
      )}
    </PlayerProfileContext.Provider>
  );
}

export function usePlayerProfile(): PlayerProfileContextValue {
  const ctx = useContext(PlayerProfileContext);
  if (!ctx) throw new Error("usePlayerProfile must be used within PlayerProfileSheetProvider");
  return ctx;
}
